import { Injectable } from '@angular/core';
import {
  combineLatest,
  fromEvent,
  Observable,
  race,
  ReplaySubject,
  Subject
} from 'rxjs';
import {
  auditTime,
  debounceTime,
  map,
  shareReplay,
  startWith,
  tap
} from 'rxjs/operators';
import MQS from 'mediaquerysensor';

export enum DeviceSizes {
  phone,
  tablet,
  desktop
}

const breakpoints: any = {
  phone: { min: 0 },
  tablet: { min: 768 },
  desktop: { min: 992 }
};

export interface Size {
  window: {
    width: number;
    height: number;
  };
  streamWrapper: { width: number; height: number };
  header: { height: number };
}

breakpoints.phone.max = breakpoints.tablet.min - 1;
breakpoints.tablet.max = breakpoints.desktop.min - 1;

@Injectable({
  providedIn: 'root'
})
export class ResponsiveService {
  currentBreakpoint = new ReplaySubject<DeviceSizes>(1);
  detectChanges$ = new ReplaySubject<boolean>(1);
  resize: Observable<Size>;
  isDesktop: Observable<boolean>;
  isTablet: Observable<boolean>;

  sizeOnLoad: number;
  public breakpointOnLoad: DeviceSizes;
  windowSize: Observable<number>;
  constructor() {
    const resize = fromEvent(window, 'resize');
    this.resize = race([resize, this.detectChanges$]).pipe(
      debounceTime(30),
      map(() => this.sizes()),
      shareReplay(1)
    );
    this.detectChanges$.next(true);
    this.isDesktop = this.currentBreakpoint.pipe(
      map(breakpoint => breakpoint === DeviceSizes.desktop)
    );

    this.isTablet = this.currentBreakpoint.pipe(
      map(breakpoint => breakpoint === DeviceSizes.tablet)
    );
    this.sizeOnLoad = window.innerWidth;
    this.breakpointOnLoad = this.sizeToBreakpoint(this.sizeOnLoad);
    this.currentBreakpoint.next(this.breakpointOnLoad);

    this.windowSize = fromEvent(window, 'resize').pipe(
      auditTime(30),
      shareReplay(1),
      map((e: any) => e.target.innerWidth)
    );

    MQS.add({
      ref: 'desktop',
      mediaQuery: `(min-width: ${breakpoints.desktop.min}px)`, // Your Media Query
      action: () => {
        this.currentBreakpoint.next(DeviceSizes.desktop);
      }
    });

    MQS.add({
      ref: 'tablet',
      mediaQuery: `(min-width: ${breakpoints.tablet.min}px) and (max-width: ${breakpoints.tablet.max}px)`,
      action: () => {
        this.currentBreakpoint.next(DeviceSizes.tablet);
      }
    });

    MQS.add({
      ref: 'phone',
      mediaQuery: `(min-width: ${breakpoints.phone.min}) and (max-width: ${breakpoints.phone.max}px)`,
      action: () => {
        this.currentBreakpoint.next(DeviceSizes.phone);
      }
    });
  }

  private sizes() {
    const streamWrapperEl = document.querySelector('.stream-wrapper-header');
    const appHeaderEl = document.querySelector('app-header');
    return {
      window: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      header: { height: appHeaderEl?.clientHeight ?? 0 },
      streamWrapper: {
        width: streamWrapperEl?.clientWidth,
        height: streamWrapperEl?.clientHeight
      }
    };
  }

  private sizeToBreakpoint(size: number): DeviceSizes {
    let breakpoint;
    if (this.sizeOnLoad > breakpoints.desktop.min) {
      breakpoint = DeviceSizes.desktop;
    } else if (this.sizeOnLoad > breakpoints.tablet.min) {
      breakpoint = DeviceSizes.tablet;
    } else {
      breakpoint = DeviceSizes.phone;
    }

    return breakpoint;
  }

  detectChanges() {
    this.detectChanges$.next(true);
  }
}

export { breakpoints };
